import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check } from "@phosphor-icons/react";
import { useQuote } from "@/lib/quote";
import { PALETTES } from "@/data/content";

function TablescapePreview({ palette, monogram }) {
  const [, linen, accent, floral, ink] = palette.colors;
  return (
    <div className="marble-soft flex h-full min-h-[400px] flex-col items-center justify-center overflow-hidden border border-ink/10 p-8" data-testid="atelier-preview">
      <div className="relative w-full max-w-sm">
        <div className="absolute inset-x-6 bottom-8 top-10" style={{ background: linen }} aria-hidden="true" />
        <div className="relative mx-auto flex aspect-[4/3] w-full items-center justify-center">
          <div className="absolute left-8 top-1/2 h-24 w-16 -translate-y-1/2 rotate-[-6deg] border border-ink/10" style={{ background: accent }} aria-hidden="true" />
          <div className="absolute right-10 top-10 flex flex-col gap-1.5" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span key={i} className="h-2 w-2" style={{ background: floral }} />
            ))}
          </div>
          <div className="relative z-10 border border-ink/15 bg-porcelain px-8 py-10 text-center shadow-[0_18px_50px_rgba(51,50,46,0.16)]">
            <div className="absolute inset-1.5 border border-ink/10" aria-hidden="true" />
            <p className="text-[9px] uppercase tracking-widest2" style={{ color: ink + "99" }}>
              Together with
            </p>
            <p className="mt-3 font-display text-4xl italic leading-none" style={{ color: accent === "#F7F5EF" ? "#67672D" : accent }} data-testid="atelier-preview-monogram">
              {monogram || "M&V"}
            </p>
            <div className="mx-auto my-4 h-px w-10" style={{ background: accent === "#F7F5EF" ? "#67672D" : accent }} aria-hidden="true" />
            <p className="font-display text-lg font-light" style={{ color: ink }}>Menu</p>
            <p className="mt-1 text-[9px] uppercase tracking-[0.2em]" style={{ color: ink + "77" }}>
              dinner &amp; dancing
            </p>
          </div>
        </div>
      </div>
      <p className="mt-6 text-[10px] uppercase tracking-widest2 text-ink/60">
        Concept preview — {palette.name} — marble &amp; linen surface
      </p>
    </div>
  );
}

export default function PaletteAtelier() {
  const { atelierOpen, setAtelierOpen, palette, savePalette, setDrawerOpen } = useQuote();
  const [selected, setSelected] = useState(palette?.name || PALETTES[0].name);
  const [monogram, setMonogram] = useState(palette?.monogram || "");

  const active = PALETTES.find((p) => p.name === selected) || PALETTES[0];

  const save = () => {
    savePalette({ name: active.name, colors: active.colors, monogram: monogram.toUpperCase().slice(0, 3) });
    setAtelierOpen(false);
    setDrawerOpen(true);
  };

  return (
    <AnimatePresence>
      {atelierOpen && (
        <motion.div
          className="fixed inset-0 z-[90] overflow-y-auto bg-ivory"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          data-testid="palette-atelier"
          role="dialog"
          aria-label="The Palette Atelier"
        >
          <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
            <div className="flex items-center justify-between border-b border-ink/10 pb-4">
              <p className="text-[11px] uppercase tracking-widest2 text-smoke">Marble &amp; Veil — The Palette Atelier</p>
              <button data-testid="atelier-close" onClick={() => setAtelierOpen(false)} aria-label="Close the Atelier" className="flex h-10 w-10 items-center justify-center text-ink/60 hover:text-ink">
                <X size={26} weight="light" />
              </button>
            </div>

            <div className="mt-10 grid gap-12 pb-20 lg:grid-cols-2 lg:gap-16">
              <div>
                <h2 className="font-display text-4xl font-light leading-tight text-ink sm:text-5xl">
                  We test the waters on color <em className="italic text-olive">together</em>.
                </h2>
                <p className="mt-5 max-w-lg leading-relaxed text-smoke">
                  Choose a direction below, try your monogram, and save what you love to your inquiry.
                  Our design offers templates we work around to keep things simple — then
                  personalization is created together, so nothing feels borrowed.
                </p>

                <div className="mt-10 space-y-3" data-testid="atelier-palette-list">
                  {PALETTES.map((p) => (
                    <button
                      key={p.name}
                      data-testid={`atelier-palette-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setSelected(p.name)}
                      className={`flex w-full items-center gap-4 border px-5 py-4 text-left transition-all duration-300 ${
                        selected === p.name ? "border-olive bg-porcelain shadow-[inset_0_0_0_1px_#67672d]" : "border-ink/15 hover:border-ink/40"
                      }`}
                      aria-pressed={selected === p.name}
                    >
                      <span className="flex">
                        {p.colors.map((c, i) => (
                          <span key={i} className="h-8 w-8 border border-ivory" style={{ background: c, marginLeft: i ? -2 : 0 }} aria-hidden="true" />
                        ))}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-display text-xl font-light leading-tight text-ink">{p.name}</span>
                        <span className="block text-[12px] text-smoke">{p.mood}</span>
                      </span>
                      {selected === p.name && <Check size={18} weight="light" className="ml-auto text-olive" />}
                    </button>
                  ))}
                </div>

                <div className="mt-10 border border-ink/15 p-6" data-testid="atelier-monogram-box">
                  <label htmlFor="atelier-monogram" className="mv-label">
                    Try your monogram
                  </label>
                  <input
                    id="atelier-monogram"
                    data-testid="atelier-monogram-input"
                    className="mv-input max-w-[180px] text-center font-display text-3xl italic text-olive"
                    value={monogram}
                    maxLength={3}
                    onChange={(e) => setMonogram(e.target.value.replace(/[^a-zA-Z&]/g, "").toUpperCase())}
                    placeholder="M&V"
                  />
                  <p className="mt-2 text-[12px] text-smoke">Two or three initials — we will draw it into the paper goods together.</p>
                </div>

                <div className="mt-10 flex flex-wrap gap-4">
                  <button
                    data-testid="atelier-save-button"
                    onClick={save}
                    className="border border-olive bg-olive px-9 py-3.5 text-[12px] uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-transparent hover:text-olive"
                  >
                    Save These Preferences to My Inquiry
                  </button>
                  <Link
                    to="/quote"
                    data-testid="atelier-build-collection-link"
                    onClick={() => setAtelierOpen(false)}
                    className="border border-ink/25 px-9 py-3.5 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-olive hover:text-olive"
                  >
                    Build My Collection
                  </Link>
                </div>
              </div>

              <div className="lg:sticky lg:top-24 lg:self-start">
                <TablescapePreview palette={active} monogram={monogram.toUpperCase()} />
                <p className="mt-5 text-[12px] leading-relaxed text-smoke">
                  This is a concept preview of your direction — not a final design. Every palette,
                  monogram, and template is refined with you before anything is produced.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
